import { useEffect, useState, useSyncExternalStore } from 'react';
import { MessageCircle, Mail, Calendar, ListTodo } from '../../icons';
import {
  getMySettings, patchMySettings, type UserSettingsDto, type PatchSettingsInput,
} from '../../api/users';
import {
  tSettings, getSettingsLang, subscribeSettingsLang, type SettingsI18nKey,
} from '../../lib/settings-i18n';

/**
 * 알림 종류 — 채팅 · 메일 · 캘린더 · 업무 (2026-08-12 통합설정 이관).
 *
 * 통합설정(`/settings` 일반 탭 `EnvironmentSection`)에 테마·언어와 한 덩어리로 묶여 있던
 * **실제로 서버에 저장되는** 알림 스위치 4종을 프로필 설정의 '알림설정' 으로 가져온 것이다.
 * 알림을 끄러 통합설정까지 갈 이유가 없다 — 개인 설정은 프로필 설정 한 곳으로 모은다.
 *
 * 저장은 원본과 **같은 경로·같은 규칙**: `patchMySettings` + 500ms 디바운스(빠른 연타로
 * 서버를 두드리지 않게). 원본 `EnvironmentSection` 은 무수정으로 남겨 둔다(통합설정은 추후
 * 재정의 예정 — 그때 이 화면이 SSOT 가 된다).
 */

type NotifKey = 'notifChat' | 'notifMail' | 'notifCalendar' | 'notifTask';

const KINDS: ReadonlyArray<{ key: NotifKey; labelKey: SettingsI18nKey; icon: typeof Mail }> = [
  { key: 'notifChat', labelKey: 'settings.env.label_notif_chat', icon: MessageCircle },
  { key: 'notifMail', labelKey: 'settings.env.label_notif_mail', icon: Mail },
  { key: 'notifCalendar', labelKey: 'settings.env.label_notif_calendar', icon: Calendar },
  { key: 'notifTask', labelKey: 'settings.env.label_notif_task', icon: ListTodo },
];

export function NotifKindsField() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const [settings, setSettings] = useState<UserSettingsDto | null>(null);
  const [pendingPatch, setPendingPatch] = useState<PatchSettingsInput | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMySettings()
      .then((s) => { if (!cancelled) setSettings(s); })
      .catch((err) => { if (!cancelled) setError(tSettings('settings.env.err_load', { msg: err.message })); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!pendingPatch) return;
    const handle = setTimeout(() => {
      patchMySettings(pendingPatch)
        .then((s) => { setSettings(s); setError(null); setPendingPatch(null); })
        .catch((err) => { setError(tSettings('settings.env.err_save', { msg: err.message })); setPendingPatch(null); });
    }, 500);
    return () => clearTimeout(handle);
  }, [pendingPatch]);

  function toggle(key: NotifKey) {
    if (!settings) return;
    const next = !settings[key];
    setSettings({ ...settings, [key]: next });
    setPendingPatch({ ...(pendingPatch ?? {}), [key]: next });
  }

  return (
    <section className="settings-section" aria-labelledby="st-notifkinds-h2">
      <h2 id="st-notifkinds-h2">{tSettings('settings.modal.notif.kinds')}</h2>
      <p className="settings-section-subtitle">{tSettings('settings.modal.notif.kinds_desc')}</p>

      <div className="st-rows">
        {KINDS.map(({ key, labelKey, icon: Icon }) => {
          const on = settings ? settings[key] : false;
          const label = tSettings(labelKey);
          return (
            <div key={key} className="st-row">
              <span className="st-row__ico" aria-hidden><Icon size={15} /></span>
              <span className="st-row__main">
                <span className="st-row__title">{label}</span>
              </span>
              <button
                type="button"
                className={`st-switch${on ? ' is-on' : ''}`}
                role="switch"
                aria-checked={on}
                aria-label={label}
                disabled={!settings}
                onClick={() => toggle(key)}
              />
            </div>
          );
        })}
      </div>

      {error && <p className="settings-error" role="alert">{error}</p>}
      {pendingPatch && <p className="settings-success">{tSettings('settings.env.msg_pending')}</p>}
    </section>
  );
}
